import { Box, Link as MuiLink, Typography, Breadcrumbs as MuiBreadcrumbs, useTheme } from '@mui/material';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useIntl } from 'react-intl';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { ProfileType } from 'types/shared/profile-type.enum';
import { getEnumValuesAsArray } from 'utils/enum';

type BreadcrumbsProps = {
  isBack?: boolean;
  crumbs?: Crumb[] | undefined;
  hideArrow?: boolean;
};

type Crumb = {
  text: string;
  url: string;
  onClick?: () => void;
};

// #region CONSTANTS

const textStyle = {
  fontStyle: 'normal',
  fontSize: 16,
  fontWeight: 500,
  lineHeight: '150%',
};

const breadcrumbsBox = {
  display: 'flex',
  alignItems: 'center',
  mt: 1,
};

// dictionary of partial urls for mapping translations
const siteGroups: { [key: string]: string } = {
  ['feedmanagement']: 'features.feedManagement',
  ['order-feed']: 'features.orderFeed',
  ['fish-check']: 'features.fishCheck',
  ['planning-prognosis']: 'features.planningPrognosis',
  ['profiles-strategies']: 'features.profilesStrategies',
  ['bluviu-nutrition']: 'features.bluViuNutrition',
  ['powerbi']: 'features.powerBi',
  ['settings']: 'features.settings',
  ['file-upload']: 'features.seaCloudFileUpload',
  ['file-sharing']: 'features.fileSharing',

  ['production-systems']: 'features.productionSystems',
  ['benchmark-analytics']: 'features.benchmarkAnalytics',
  ['self-service']: 'features.selfService',
  ['shrimp-upload']: 'features.shrimpDataUpload',
  ['playground']: 'features.playground',
};

// dictionary of all routes and their corresponding translation id, excluding optional parameters
const routeDict: { [key: string]: string } = {
  // Overview
  '/overview': 'features.overview',

  // Order Feed
  '/order-feed/order-history': 'orderFeed.navigation.orderHistory',
  '/order-feed/purchase-history': 'orderFeed.navigation.purchaseHistory',
  '/order-feed/starvation': 'orderFeed.navigation.listStarvation',
  '/order-feed/supply-plan': 'orderFeed.navigation.orderSupplyPlan',
  '/order-feed/order-distribution': 'orderFeed.navigation.orderDistribution',
  '/order-feed/timeline': 'orderFeed.navigation.timeline',

  // Fish Check
  '/fish-check/welfare-scoring': 'fishCheck.navigation.listWelfareScoring',

  // Planning and Prognosis
  '/planning-prognosis/notifications': 'planningPrognosis.navigation.listNotifications',
  '/planning-prognosis/notifications/active': 'planningPrognosis.tabs.active',
  '/planning-prognosis/notifications/historic': 'planningPrognosis.tabs.historic',
  '/planning-prognosis/sites': 'planningPrognosis.navigation.listSites',
  '/planning-prognosis/sites/active': 'planningPrognosis.tabs.activeSite',
  '/planning-prognosis/sites/harvested': 'planningPrognosis.tabs.harvested',
  '/planning-prognosis/sites/others': 'planningPrognosis.tabs.other',

  // Power BI
  '/powerbi/reports': 'powerBi.navigation.listReports',

  // File Sharing
  '/file-sharing': 'fileSharing.title',

  // File Upload
  '/file-upload': 'seaCloudFileUpload.navigation.viewFileSummary',

  // bluViu Nutrition
  '/bluviu-nutrition/r2': 'bluViuNutrition.navigation.listBluViuNutritionR2',
  '/bluviu-nutrition/scenarios': 'bluViuNutrition.navigation.listScenarios',
  '/bluviu-nutrition/calibrations': 'bluViuNutrition.navigation.listCalibrations',
  '/bluviu-nutrition/scenarios/result': 'bluViuNutrition.navigation.viewScenarioResultGt2',

  // FeedManagement
  '/feed-management/customers': 'feedManagement.navigation.listCustomers',
  '/feed-management/nutrients': 'feedManagement.navigation.listNutrients',
  '/feed-management/plants': 'feedManagement.navigation.listPlants',
  '/feed-management/bestmix-feeds': 'feedManagement.navigation.listBestMixFeeds',
  '/feed-management/manual-feeds': 'feedManagement.navigation.listManualFeeds',

  // Site Settings
  '/site-settings/sites': 'features.siteSettings',
  '/site-settings/inactive-sites': 'features.siteSettings',
  '/site-settings/sites-without-mapping': 'features.siteSettings',
  '/site-settings/sites/general': 'siteSettings.navigation.generalSettings',
  '/site-settings/sites/inventory-silo-management': 'siteSettings.navigation.inventorySiloManagement',
  '/site-settings/sites/contact-details': 'siteSettings.navigation.contactDetails',

  // Profiles and Strategies
  '/profiles-strategies/feed-strategies': 'profilesStrategies.navigation.listFeedStrategies',
  '/profiles-strategies/feed-strategies/create': 'profilesStrategies.createFeedStrategies.title',
  '/profiles-strategies/market': 'profilesStrategies.navigation.editMarketProfiles',
  '/profiles-strategies/profiles/efi': 'profilesStrategies.createProfiles.profiles.efi',
  '/profiles-strategies/profiles/egi': 'profilesStrategies.createProfiles.profiles.egi',
  '/profiles-strategies/profiles/mort': 'profilesStrategies.createProfiles.profiles.mort',
  '/profiles-strategies/profiles': 'profilesStrategies.listProfiles.title',
  '/profiles-strategies/profiles/create': 'profilesStrategies.createProfiles.title',
  '/profiles-strategies/site': 'profilesStrategies.navigation.editSiteProfiles',

  //#region Shrimps

  // Production Systems
  '/production-systems/farm-overview': 'productionSystems.farmOverview.title',
  '/production-systems/registration-log': 'productionSystems.registrationLog.title',
  '/production-systems/farm-setup': 'productionSystems.farmSetup.title',

  // Self Service
  '/self-service/feeders': 'selfService.feeders.title',
  '/self-service/aerators': 'selfService.aerators.title',
  '/self-service/feed-catalog': 'selfService.feedCatalog.title',
  '/self-service/treatment-catalog': 'selfService.treatmentCatalog.title',

  // Shrimp Data Upload
  '/shrimp-upload': 'shrimpDataUpload.navigation.viewShrimpDataUpload',

  // Benchmark Analytics
  '/benchmark-analytics/growth': 'benchmarkAnalytics.growth',
  '/benchmark-analytics/placeholder-one': 'benchmarkAnalytics.placeholderOne',
  '/benchmark-analytics/placeholder-two': 'benchmarkAnalytics.placeholderTwo',

  //#endregion Shrimps

  // #region Playground
  '/playground/supply-plan': 'orderFeed.navigation.orderSupplyPlan',
};

const playgroundRouteLabels: Record<string, string> = {
  '/playground/data-lab': 'Data Lab',
  '/playground/data-lab/gallery': 'Gallery',
  '/playground/data-lab/drill-down': 'Drill-down',
  '/playground/data-lab/flow': 'Flow',
  '/playground/data-lab/embedded': 'Embedded',
  '/playground/data-lab/controls': 'Controls',
};

const empty = 'general.empty';

// matches purely numeric ids (ie order numbers) and guids (ie globalSiteId, scenarioId)
const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isDynamicIdSegment = (urlPart: string) => /^\d+$/.test(urlPart) || guidPattern.test(urlPart);

// #endregion CONSTANTS

// #region RENDERS

// Each individual "crumb" in the breadcrumbs list
const Crumb = ({ text, url, onClick, last = false }: { text: string; url: string; onClick?: () => void; last: boolean }) => {
  const theme = useTheme();
  // The last crumb is rendered as normal text since we are already on the page
  if (last) {
    return (
      <Typography
        sx={{
          ...textStyle,
          [theme.breakpoints.down('sm')]: {
            fontSize: 14,
          },
        }}
        color="#DEBFA2">
        {text}
      </Typography>
    );
  }

  // All other crumbs will be rendered as links that can be visited
  return (
    <MuiLink component={RouterLink} to={url} onClick={onClick} color="white" underline="none">
      <Typography sx={textStyle}>{text}</Typography>
    </MuiLink>
  );
};

// #endregion RENDERS

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ crumbs, isBack = true, hideArrow = false }) => {
  const history = useHistory();
  const intl = useIntl();

  // #region HELPERS

  const getCrumbText = (path: string) =>
    playgroundRouteLabels[path] || intl.formatMessage({ id: routeDict[path] || empty });

  // const routeDictionary: { [key: string]: string } =
  //   routes.reduce((acc, curr) => {
  //     let paramStartIndex = curr.path.indexOf(':');
  //     let path: string = curr.path.substring(0, paramStartIndex > 0 ? paramStartIndex : undefined);
  //     path = path.endsWith('/') ? path.slice(0, -1) : path;

  //     if (Object.keys(acc).includes(path)) {
  //       return { ...acc };
  //     } else {
  //       return { ...acc, [path]: curr.id };
  //     }
  //   }, {}) || {};

  const generateBreadcrumbs = () => {
    let url = history.location.pathname;
    // strip dynamic id segments (numeric ids and guids) but keep route names that merely contain digits, e.g. "r2"
    let urlList = url.split('/').filter((x) => !!x && !isDynamicIdSegment(x));
    // starting url each crumb will use and build on in the loop
    let start = '/';
    // breadcrumbs to be returned
    let crumbObjects: { path: string; text: string; onClick?: (() => void) | undefined }[] = [];

    urlList.forEach((urlPart, idx) => {
      if (idx === 0) {
        if (Object.keys(siteGroups).includes(urlPart)) {
          let text = intl.formatMessage({ id: siteGroups[urlPart] }) || empty;
          let path = start + urlPart;

          crumbObjects.push({
            path: path,
            text: text,
          });
        }
        return;
      }

      let prev = urlList[idx - 1]; // get prev url part to begin current url ie '/order-feed'

      start += prev + '/'; // update start to next url ie '/order-feed/'

      let path = start + urlPart; // combine prev and current url, ie '/order-feed/supply-plan'
      // check if partial url ie '/order-feed' since this route does not actually exist. we force redirect via <Dashboard from to />
      if (Object.keys(siteGroups).includes(urlPart)) {
        let text = intl.formatMessage({ id: siteGroups[urlPart] }) || empty;

        crumbObjects.push({
          path: path,
          text: text,
        });
        return;
      }

      // #region Fringe Cases
      // Supply Plan
      if (
        prev === 'supply-plan' &&
        (urlPart === 'edit' || urlPart === 'create-order' || urlPart === 'order-free-delivery' || urlPart === 'sites')
      ) {
        return;
      }

      // hacky fix for nested sub tabs
      const mySupplyPlanTabs = ['order-free-delivery'];
      const mySupplyPlanSubTabs = ['orders', 'input-settings'];

      if (mySupplyPlanTabs.includes(prev) && mySupplyPlanSubTabs.includes(urlPart)) {
        return;
      }

      // bluViu Nutrition
      if (prev === 'calibrations' && (urlPart === 'simple' || urlPart === 'multi')) return;
      if (prev === 'r2' && (urlPart === 'setup' || urlPart === 'results' || urlPart === 'applied')) return;

      if (prev === 'profiles' && getEnumValuesAsArray(ProfileType).includes(urlPart)) {
        crumbObjects.push({
          path: start,
          text: getCrumbText(path),
        });

        return;
      }

      if (prev === 'feed-program-planner') {
        crumbObjects.push({
          path: start,
          text: getCrumbText(path),
        });

        return;
      }

      // hacky fix for url building with nested tabs. dynamic url part is before the following tabs, but are not added to the crumbs until the end. find out how to account for this.
      const planningPrognosisTabs = ['fishgroups', 'virtual', 'harvested', 'calculator', 'profiles'];
      const planningPrognosisParentTabs = ['active', 'harvested', 'virtual', 'others'];

      if (planningPrognosisParentTabs.includes(prev) && planningPrognosisTabs.includes(urlPart)) {
        return;
      }

      const checkModel = url.includes('/bluviu-nutrition/scenarios');

      if (checkModel && url.endsWith('result') && urlPart === 'result') {
        return;
      }

      // #endregion Fringe Cases

      crumbObjects.push({
        path: path,
        text: getCrumbText(path),
      });
    });

    crumbs?.forEach((crumb) => {
      let prev = crumbObjects[crumbObjects.length - 1]?.path || empty;

      crumbObjects.push({ path: `${prev}/${crumb.url}`, text: crumb.text, onClick: crumb.onClick });
    });

    return crumbObjects;
  };

  // #endregion HELPERS

  const breadcrumbs = generateBreadcrumbs();

  return (
    <Box sx={breadcrumbsBox}>
      {hideArrow ||
        (isBack && breadcrumbs.length > 2 && (
          <MuiLink
            sx={{ height: 21 }}
            component={RouterLink}
            to={breadcrumbs[breadcrumbs.length > 2 ? breadcrumbs.length - 2 : 0].path}>
            <ArrowLeft color="white" size={25} />
          </MuiLink>
        ))}
      <MuiBreadcrumbs separator={<ChevronRight size={18} color="white" />} sx={{ ml: 2, mt: 2 }}>
        {breadcrumbs.map((crumb, idx) => (
          <Crumb text={crumb.text} url={crumb.path} key={idx} onClick={crumb.onClick} last={idx === breadcrumbs.length - 1} />
        ))}
      </MuiBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs;